import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EnelXGui } from 'gui-com-lib';
import { Header, StyledButton } from '../../main.styled';
import { confirmationOffer } from '../../api';
import { toastError } from '../common/ToastError';
import { CustomModal } from '../CustomModal';
import { parseData } from './utils';
import {
  Wrap,
  Card,
  Title,
  SubTitle,
  Hint,
  Table,
  THead,
  TBody,
  TableRow,
  TableCell,
  ProductName,
  Kwh,
  Gift,
  CheckboxWrapper,
  Checkbox,
  FootNote,
  CtaWrapper,
  ConfirmButton,
} from './SplitHomeSelection.styled';

const { Loader } = EnelXGui.EnelXGuiComponents;
const { Button } = EnelXGui.EnelXGuiAtoms;

const SplitHomeSelection = () => {
  const { t } = useTranslation();
  const params = new URLSearchParams(document.location.search);
  const customer = params.get('customer') || '';

  const [data, setData] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [registering, setRegistering] = useState(true);
  const [modalResult, setModalResult] = useState(false);
  const [statusCode, setStatusCode] = useState('');

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/splithomeSelection.json`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch(() => toastError(t('Errore imprevisto, riprovare più tardi')));
  }, [t]);

  const parsed = data ? parseData(data) : null;
  const {
    maxSelections: MAX_SELECTIONS, deviceCategories: DEVICE_CATEGORIES, statusCode200: STATUS_CODE_200_MAPPER, statusCodeError: STATUS_CODE_ERROR_MAPPER,
  } = parsed || {};

  const limitReached = selectedDevices.length >= (MAX_SELECTIONS || 0);

  const handleCheckboxChange = (deviceType) => {
    setSelectedDevices((prev) => {
      if (prev.includes(deviceType)) {
        return prev.filter((d) => d !== deviceType);
      }
      if (prev.length >= MAX_SELECTIONS) {
        return prev;
      }
      return [...prev, deviceType];
    });
  };

  const confirmOffer = async () => {
    const body = {
      productFamily: 'SPLIT_HOME',
      deviceTypes: selectedDevices,
    };
    const headers = { 'x-UniqueId': customer };
    const requestParams = { api_key: process.env.REACT_APP_API_KEY };

    try {
      setIsFetching(true);
      const answer = await confirmationOffer(body, headers, requestParams);
      const code = answer?.data?.status_code?.code;
      setStatusCode(code);
      setModalResult(true);
      setRegistering(false);
      setIsFetching(false);
    } catch (err) {
      setIsFetching(false);
      const description = err?.response?.data?.status_code?.description;
      toastError(t(STATUS_CODE_ERROR_MAPPER[description] || data.defaultError));
    }
  };

  const onCloseModal = () => {
    setRegistering(true);
    setModalResult(false);
    setStatusCode('');
    setSelectedDevices([]);
  };

  const isSuccess = String(statusCode) === '0';

  return (
    <>
      {(isFetching || !data) && <Loader isSpinning />}
      {data && registering && (
        <Wrap>
          <Card>
            <Title>
              {t(data.tableTitle)}
            </Title>
            <SubTitle>
              {t(data.tableSubtitle)}
            </SubTitle>
            <Hint>
              {`${t(data.tableHint)} `}
              <b>{`${t(data.tableHintBold)}.`}</b>
            </Hint>

            <Table aria-label="Selezione prodotto">
              <THead>
                <tr>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <th />
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <th />
                  <th>{data.tableColumnKwh}</th>
                  <th>{t(data.tableColumnGift)}</th>
                </tr>
              </THead>
              <TBody>
                {DEVICE_CATEGORIES.map((cat) => {
                  const isSelected = selectedDevices.includes(cat.deviceType);
                  const isDisabled = !isSelected && limitReached;

                  return (
                    <TableRow key={cat.key} disabled={isDisabled}>
                      <TableCell>
                        <CheckboxWrapper>
                          <Checkbox
                            aria-label={`Seleziona ${cat.label}`}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleCheckboxChange(cat.deviceType)}
                          />
                        </CheckboxWrapper>
                      </TableCell>
                      <TableCell>
                        <ProductName>{cat.label}</ProductName>
                      </TableCell>
                      <TableCell><Kwh>{cat.kwh}</Kwh></TableCell>
                      <TableCell>
                        {/* eslint-disable-next-line react/no-danger */}
                        <Gift dangerouslySetInnerHTML={{ __html: cat.gift }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TBody>
            </Table>

            <FootNote>
              {data.footNotes.map((note) => (
                <p key={note.slice(0, 20)}>{t(note)}</p>
              ))}
            </FootNote>
          </Card>

          <CtaWrapper>
            <ConfirmButton
              disabled={selectedDevices.length === 0}
              onClick={confirmOffer}
            >
              {t(data.confirmButton)}
            </ConfirmButton>
          </CtaWrapper>
        </Wrap>
      )}
      {modalResult && (
        <CustomModal
          height="auto"
          width="432px"
          className="modal"
          closeIcon
          closeModal={onCloseModal}
          render={() => (
            <>
              <Header medium center>
                {t(STATUS_CODE_200_MAPPER[statusCode] || STATUS_CODE_200_MAPPER[String(statusCode)])}
              </Header>
              {isSuccess && (
                <StyledButton>
                  <Button
                    btnType="primary"
                    btnText={t(data.successButton)}
                    onClick={onCloseModal}
                  />
                </StyledButton>
              )}
              {!isSuccess && (
                <StyledButton>
                  <Button
                    btnType="primary"
                    btnText={t(data.closeButton)}
                    onClick={onCloseModal}
                  />
                </StyledButton>
              )}
            </>
          )}
        />
      )}
    </>
  );
};

export { SplitHomeSelection };
